import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TableSearchInput as ExternalInput } from '../../../components/_mui';
import { BackorderFilterEnum } from '../../../constants/inventories';
import { INITIAL_PAGE } from '../../../constants/table';
import { DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN } from '../../../constants/users';
import useDeptFilter from '../../../hooks/useDeptFilter';
import useSearchFilter from '../../../hooks/useSearchFilter/useSearchFilter';
import { fetchInventoryItemsRequest } from '../../../store/actions/inventories';
import { changeInventoryActiveView } from '../../../store/actions/shell';
import { selectAdminType } from '../../../store/selectors/auth';
import { selectFulfillmentCenterItems } from '../../../store/selectors/fulfillmentCenters';
import {
  selectInventoryItems,
  selectInventoryTotalCount,
  selectIsFinalInventoryItem,
  selectIsInventoryFetching,
} from '../../../store/selectors/inventories';
import { selectInventoryActiveView } from '../../../store/selectors/shell';
import {
  IFetchInventoryItemsRequestPayload,
  IInventoryFilters,
  InventoryFilterTypesEnum,
} from '../../../types/inventories';
import { FilterTypesEnum, ViewTypesEnum } from '../../../types/shell';
import { IFiltersRef } from '../../../types/tables';
import { hasPermission } from '../../../utils/users';
import Filters from './Filters';

interface IProps {
  currentPageSize: number;
  renderDownloadButton: React.JSX.Element | null;
  renderAddButton: React.JSX.Element | null;
}

const useFilters = ({ currentPageSize, renderDownloadButton, renderAddButton }: IProps) => {
  const dispatch = useDispatch();

  const filtersRef = useRef<IFiltersRef>(null);

  const adminType = useSelector(selectAdminType);
  const isFinalInventoryItem = useSelector(selectIsFinalInventoryItem);
  const isInventoryFetching = useSelector(selectIsInventoryFetching);
  const inventoryTotalCount = useSelector(selectInventoryTotalCount);
  const activeView = useSelector(selectInventoryActiveView);
  const fulfillmentCenters = useSelector(selectFulfillmentCenterItems);

  const [selectedType, setSelectedType] = useState<InventoryFilterTypesEnum | string>('');
  const [selectedFulfillmentCenter, setSelectedFulfillmentCenter] = useState('');
  const [backorderStatus, setBackorderStatus] = useState<BackorderFilterEnum | string>('');

  const inventoryItems = useSelector(selectInventoryItems(InventoryFilterTypesEnum.All));

  const {
    allDepartments,
    departments: selectedDepartments,
    setDepartments: setSelectedDepartments,
  } = useDeptFilter(null, null, {});

  const { SearchInput, query, resetSearch } = useSearchFilter({
    items: [],
    fieldNames: [],
    ExternalInput,
    props: { dataAction: 'inventory_search' },
  });

  const getRequestInfo = useCallback(
    (payload?: Partial<IFetchInventoryItemsRequestPayload>): IFetchInventoryItemsRequestPayload => {
      return {
        page_size: currentPageSize,
        page: payload?.page || 1,
        ...(backorderStatus === BackorderFilterEnum.Enabled && { include_preorder: 'on' }),
        ...(backorderStatus === BackorderFilterEnum.Disabled && { include_preorder: 'off' }),
        ...(query && { search_query: query }),
        ...(selectedDepartments && typeof selectedDepartments === 'string' && { department_id: selectedDepartments }),
        ...(selectedFulfillmentCenter && { fulfillment_center_id: selectedFulfillmentCenter }),
        ...(selectedType ? { types: [selectedType as InventoryFilterTypesEnum] } : {}),
        ...payload,
      };
    },
    [currentPageSize, selectedDepartments, selectedFulfillmentCenter, selectedType, query, backorderStatus],
  );

  const fetchInventory = useCallback(
    (payload: Partial<IFetchInventoryItemsRequestPayload>) => {
      dispatch(fetchInventoryItemsRequest(getRequestInfo(payload), { shouldClearItems: payload?.page === 1 }));
    },
    [dispatch, getRequestInfo],
  );

  const filterConfig = useMemo(() => {
    const fulfillmentCenterList = fulfillmentCenters?.filter((fc) => fc.has_org_inventory) || [];

    return [
      {
        isMultiple: false,
        label: 'Type',
        tabId: FilterTypesEnum.Type,
        value: selectedType,
        isVisible: true,
        onChange: setSelectedType,
        dataAction: 'inventory_type_filter',
        options: [
          { label: 'Physical', value: InventoryFilterTypesEnum.Physical },
          { label: 'Digital', value: InventoryFilterTypesEnum.Digital },
          { label: 'MSKU', value: InventoryFilterTypesEnum.MSKU },
          { label: 'Show all', value: '' },
        ],
      },
      {
        isMultiple: false,
        label: 'Department',
        tabId: FilterTypesEnum.Department,
        value: selectedDepartments || '',
        isVisible: hasPermission([ORG_ADMIN, SUPER_ADMIN, DISTRIBUTOR], adminType),
        onChange: setSelectedDepartments,
        dataAction: 'inventory_department_filter',
        options: [...(allDepartments?.map((dep) => ({ label: dep.name, value: dep.uid })) || [])],
      },
      {
        isMultiple: false,
        label: 'Warehouse',
        tabId: FilterTypesEnum.Warehouse,
        value: selectedFulfillmentCenter || '',
        isVisible: true,
        onChange: setSelectedFulfillmentCenter,
        dataAction: 'inventory_warehouse_filter',
        options: [...fulfillmentCenterList.map((fc) => ({ label: fc.name, value: fc.uid }))],
      },
      {
        isMultiple: false,
        label: 'Backorder status',
        tabId: FilterTypesEnum.Backorder,
        value: backorderStatus || '',
        isVisible: true,
        onChange: setBackorderStatus,
        dataAction: 'inventory_backorder_status_filter',
        options: [
          { label: 'Backorder enabled', value: BackorderFilterEnum.Enabled },
          { label: 'Backorder disabled', value: BackorderFilterEnum.Disabled },
        ],
      },
    ];
  }, [
    selectedType,
    selectedDepartments,
    setSelectedDepartments,
    allDepartments,
    adminType,
    selectedFulfillmentCenter,
    fulfillmentCenters,
    backorderStatus,
  ]);

  const handleClearAllFilters = useCallback(() => {
    filterConfig.forEach((filter) => {
      filter.onChange('');
    });
    resetSearch();
  }, [filterConfig, resetSearch]);

  const setActiveView = useCallback(
    (view: ViewTypesEnum) => {
      dispatch(changeInventoryActiveView(view));
    },
    [dispatch],
  );

  const Component = React.useMemo(
    () => (
      <Filters
        filterConfig={filterConfig}
        ref={filtersRef}
        renderSearchField={SearchInput}
        query={query}
        activeView={activeView}
        setActiveView={setActiveView}
        onSearchReset={resetSearch}
        renderDownloadButton={renderDownloadButton}
        renderAddButton={renderAddButton}
      />
    ),
    [SearchInput, filterConfig, query, activeView, setActiveView, resetSearch, renderDownloadButton, renderAddButton],
  );

  const filters = useMemo<IInventoryFilters>(
    () => ({
      selectedType,
      selectedDepartments: selectedDepartments || '',
      selectedFulfillmentCenter,
      backorderStatus,
      query,
    }),
    [selectedType, selectedDepartments, selectedFulfillmentCenter, backorderStatus, query],
  );

  useLayoutEffect(() => {
    fetchInventory({
      page: INITIAL_PAGE,
      page_size: currentPageSize,
    });
  }, [filters]);

  return {
    filteredItems: inventoryItems,
    query,
    inventoryTotalCount,
    isInventoryFetching,
    filtersRef,
    onClear: handleClearAllFilters,
    Filters: Component,
    activeView,
    fetchInventory,
    isFinalInventoryItem,
    filters,
  };
};

export default useFilters;
